import React, { useMemo } from "react";

import {
  ChipProps as MUIChipsProps,
  Chip as MUIChip,
  Box,
  useTheme,
  Theme,
} from "@mui/material";

import { responsiveFontSize } from "@common/components/Typography/style";

import { Variants } from "../Button";

const getColors = (theme: Theme, variant: Variants, color?: Colors) => {
  const { palette } = theme;
  const build = (
    backgroundColor: string,
    color: string,
    borderColor?: string
  ) => ({
    backgroundColor,
    color,
    border: `1px solid ${borderColor ?? backgroundColor}`,
    "&:hover": { backgroundColor },
  });
  const buildText = (color: string) => ({
    color,
    backgroundColor: "transparent",
  });
  const buildOutlined = (color: string) => ({
    color: color,
    borderColor: color,
    borderStyle: "solid",
    borderWidth: "1px",
    backgroundColor: "transparent",
  });

  switch (color) {
    case "primary":
      return build(palette.primary.main, palette.primary.contrastText);
    case "secondary":
      return build(palette.secondary.main, palette.secondary.contrastText);
    case "info":
      return build(palette.info.main, palette.info.contrastText);
    case "warning":
      return build(palette.warning.main, palette.warning.contrastText);
    case "success":
      if (variant === "outlined") return buildOutlined(palette.success.main);
      if (variant === "text") return buildText(palette.success.main);
      return build(palette.success.main, palette.common.black);
    case "error":
      if (variant === "outlined") return buildOutlined(palette.error.main);
      if (variant === "text") return buildText(palette.error.main);
      return build(palette.error.main, palette.common.white);
    case "greyed":
      if (variant === "outlined")
        return buildOutlined(palette.common.grey.light);
      if (variant === "text") return buildText(palette.common.grey.light);
      return build(palette.common.grey.light, palette.common.black);
    case "meeting":
      if (variant === "outlined")
        return buildOutlined(palette.meeting.background);
      if (variant === "text") return buildText(palette.meeting.background);
      return build(palette.meeting.background, palette.common.white);
    case "white":
      if (variant === "outlined") return buildOutlined(palette.common.white);
      if (variant === "text") return buildText(palette.common.white);
      return build(palette.common.white, palette.common.black);
    case "Critical":
      if (variant === "outlined")
        return buildOutlined(palette.ticket.Critical.main);
      return build(palette.ticket.Critical.main, palette.common.white);
    case "Major":
      if (variant === "outlined")
        return buildOutlined(palette.ticket.Major.main);
      return build(palette.ticket.Major.main, palette.common.white);
    case "Minor":
      if (variant === "outlined")
        return buildOutlined(palette.ticket.Minor.main);
      return build(palette.ticket.Minor.main, palette.common.white);
    case "Informational":
      if (variant === "outlined")
        return buildOutlined(palette.ticket.Informational.main);
      return build(palette.ticket.Informational.main, palette.common.white);
    case "default":
      if (variant === "outlined")
        return buildOutlined(palette.common.grey.light);
      return build(palette.grey[800], palette.common.white);
    default:
      if (variant === "outlined")
        return buildOutlined(palette.common.grey.light);
      return build(palette.grey[800], palette.common.white);
  }
};

declare module "@mui/material/Chip" {
  interface ChipPropsColorOverrides {
    meeting: true;
  }
}

export type Colors =
  | "success"
  | "default"
  | "error"
  | "warning"
  | "info"
  | "secondary"
  | "primary"
  | "greyed"
  | "meeting"
  | "white"
  | "meeting"
  | "Critical"
  | "Major"
  | "Minor"
  | "Informational";

type ChipProps = {
  color?: Colors;
  radius?: "small" | "medium";
  uppercase?: boolean;
  fontSize?: "smaller" | "small" | "medium" | "big" | "bigger";
  realTime?: boolean;
  variant?: Variants;
};

type UnionType = Omit<MUIChipsProps, "color" | "size" | "variant"> & ChipProps;

const Chip = (props: React.PropsWithChildren<UnionType>) => {
  const theme = useTheme();
  const {
    radius = "medium",
    uppercase = true,
    fontSize = "medium",
    realTime = false,
    color,
    variant = "outlined",
    ...other
  } = props;

  const computedRadius = useMemo(() => {
    return (
      radius === "small" && {
        "&.MuiChip-root": {
          borderRadius: "4px",
        },
      }
    );
  }, [radius]);

  const computedUppercase = useMemo(() => {
    return (
      uppercase && {
        "&.MuiChip-root": {
          textTransform: "uppercase",
        },
      }
    );
  }, [uppercase]);

  const computedSize = useMemo(
    () => responsiveFontSize(theme, fontSize),
    [fontSize]
  );

  const computedRealtime = useMemo(() => {
    return (
      realTime && {
        "&.MuiChip-root": {
          fontFamily: "Realtime",
          paddingBottom: "0.5rem",
        },
      }
    );
  }, [realTime]);

  const colors = useMemo(
    () => getColors(theme, variant, color),
    [theme, variant, color]
  );

  return (
    <Box>
      <MUIChip
        {...other}
        sx={[
          computedRadius,
          computedUppercase,
          computedSize,
          computedRealtime,
          colors,
        ]}
      ></MUIChip>
    </Box>
  );
};

export default Chip;
